import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TransaccionService } from '../../services/transaccion.service';
import { DepositoRetenido } from '../../services/depositos-retenidos.service';

export interface DepositosRetenidosDialogData {
  /** A quién se acaba de aprobar, para el título. */
  titulo: string;
  /** Cuenta que recibe los depósitos. */
  usuarioIdDestino: number;
  /** Cómo se nombra esa cuenta en pantalla. */
  destino: string;
  depositos: DepositoRetenido[];
}

type EstadoFila = 'pendiente' | 'enviando' | 'asignado' | 'error';

interface Fila {
  deposito: DepositoRetenido;
  elegido: boolean;
  estado: EstadoFila;
  error?: string;
}

/**
 * Al aprobar a un cliente o a un pagador (A1): muestra los depósitos que
 * quedaron retenidos mientras no estaba aprobado y ofrece asignarlos a su
 * cuenta. Cada asignación pasa por las mismas validaciones que "Transacciones
 * sin asignar" (C5) y se acredita por la cola, igual que una asignación manual.
 */
@Component({
  selector: 'app-depositos-retenidos-dialog',
  templateUrl: './depositos-retenidos-dialog.component.html',
  styleUrls: ['./depositos-retenidos-dialog.component.sass'],
})
export class DepositosRetenidosDialogComponent {
  filas: Fila[];
  asignando = false;
  terminado = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DepositosRetenidosDialogData,
    private dialogRef: MatDialogRef<DepositosRetenidosDialogComponent, boolean>,
    private transacciones: TransaccionService,
  ) {
    this.filas = data.depositos.map((deposito) => ({
      deposito,
      elegido: true,
      estado: 'pendiente',
    }));
  }

  get elegidos(): Fila[] {
    return this.filas.filter((f) => f.elegido && f.estado !== 'asignado');
  }

  get montoElegido(): number {
    return this.elegidos.reduce((total, f) => total + Number(f.deposito.moSaldoAbono || 0), 0);
  }

  get asignados(): number {
    return this.filas.filter((f) => f.estado === 'asignado').length;
  }

  get fallidos(): number {
    return this.filas.filter((f) => f.estado === 'error').length;
  }

  clp(monto: number | string | null | undefined): string {
    return `$${Number(monto || 0).toLocaleString('es-CL')}`;
  }

  /** De a una: si una falla (p. ej. la regla de C5), las demás siguen. */
  async asignar(): Promise<void> {
    if (this.asignando || !this.elegidos.length) return;
    this.asignando = true;
    for (const fila of this.elegidos) {
      fila.estado = 'enviando';
      fila.error = undefined;
      try {
        await this.transacciones.assignUnassignedTrx({
          ...fila.deposito,
          usuarioId: this.data.usuarioIdDestino,
        });
        fila.estado = 'asignado';
      } catch (err: any) {
        fila.estado = 'error';
        fila.error =
          (typeof err?.error?.message === 'string' && err.error.message) ||
          'No se pudo asignar.';
      }
    }
    this.asignando = false;
    this.terminado = true;
  }

  cerrar(): void {
    this.dialogRef.close(this.asignados > 0);
  }
}
